// Catalog of lab markers the user can pin to the dashboard's right column.
// Each marker carries a matcher (`re`) used to find its latest value in the PHR
// labs, a short display label, an optional unit override, and a panel group for
// the picker UI. Presets map common conditions → a ready-made set of (≤4) markers.

export type LabMarker = { id: string; label: string; re: RegExp; unit?: string; group: string };

export const LAB_CATALOG: LabMarker[] = [
  // Complete Blood Count
  { id: 'wbc', label: 'WBC', re: /^wbc|white blood/i, unit: 'kU/μL', group: 'CBC' },
  { id: 'rbc', label: 'RBC', re: /^rbc|red blood/i, unit: 'M/μL', group: 'CBC' },
  { id: 'hgb', label: 'HGB', re: /^hgb|^hb\b|h(a)?emoglob/i, group: 'CBC' },
  { id: 'hct', label: 'HCT', re: /^hct|h(a)?ematocrit/i, group: 'CBC' },
  { id: 'plt', label: 'Platelets', re: /^plt|platelet/i, group: 'CBC' },
  { id: 'mcv', label: 'MCV', re: /^mcv/i, group: 'CBC' },

  // Basic Metabolic Panel
  { id: 'glucose', label: 'Glucose', re: /glucose|fasting (blood )?sugar|^fbs/i, unit: 'mg/dL', group: 'Metabolic panel' },
  { id: 'sodium', label: 'Sodium', re: /^sodium|^na\b/i, unit: 'mmol/L', group: 'Metabolic panel' },
  { id: 'potassium', label: 'Potassium', re: /^potassium|^k\b/i, unit: 'mmol/L', group: 'Metabolic panel' },
  { id: 'chloride', label: 'Chloride', re: /^chloride|^cl\b/i, unit: 'mmol/L', group: 'Metabolic panel' },
  { id: 'co2', label: 'CO₂', re: /^co2|bicarb/i, group: 'Metabolic panel' },
  { id: 'bun', label: 'BUN', re: /^bun|urea nitrogen/i, unit: 'mg/dL', group: 'Metabolic panel' },
  { id: 'creatinine', label: 'Creatinine', re: /creatinin/i, unit: 'mg/dL', group: 'Metabolic panel' },
  { id: 'egfr', label: 'eGFR', re: /egfr|glomerular/i, group: 'Metabolic panel' },
  { id: 'calcium', label: 'Calcium', re: /^calcium|^ca\b/i, unit: 'mg/dL', group: 'Metabolic panel' },

  // Diabetes & lipids
  { id: 'a1c', label: 'A1c', re: /a1c|glycated|hba1c/i, unit: '%', group: 'Diabetes & lipids' },
  { id: 'chol', label: 'Cholesterol', re: /total chol|^cholesterol/i, unit: 'mg/dL', group: 'Diabetes & lipids' },
  { id: 'hdl', label: 'HDL', re: /hdl/i, unit: 'mg/dL', group: 'Diabetes & lipids' },
  { id: 'ldl', label: 'LDL', re: /ldl/i, unit: 'mg/dL', group: 'Diabetes & lipids' },
  { id: 'trig', label: 'Triglycerides', re: /triglycer/i, unit: 'mg/dL', group: 'Diabetes & lipids' },

  // Inflammatory markers
  { id: 'crp', label: 'CRP', re: /^crp|c[-\s]?reactive/i, unit: 'mg/L', group: 'Inflammatory' },
  { id: 'esr', label: 'ESR', re: /^esr|sed(imentation)? rate/i, unit: 'mm/hr', group: 'Inflammatory' },
  { id: 'ferritin', label: 'Ferritin', re: /ferritin/i, unit: 'ng/mL', group: 'Inflammatory' },

  // Liver function
  { id: 'alt', label: 'ALT', re: /^alt\b|alanine/i, unit: 'U/L', group: 'Liver' },
  { id: 'ast', label: 'AST', re: /^ast\b|aspartate/i, unit: 'U/L', group: 'Liver' },
  { id: 'alp', label: 'ALP', re: /^alp\b|alkaline phos/i, unit: 'U/L', group: 'Liver' },
  { id: 'bili', label: 'Bilirubin', re: /bilirubin/i, unit: 'mg/dL', group: 'Liver' },

  // Thyroid
  { id: 'tsh', label: 'TSH', re: /^tsh|thyroid stim/i, unit: 'mIU/L', group: 'Thyroid' },
  { id: 'ft4', label: 'Free T4', re: /free t4|ft4/i, group: 'Thyroid' },
];

export const LAB_PRESETS: { id: string; label: string; markers: string[] }[] = [
  { id: 'infection', label: 'Infection', markers: ['wbc', 'rbc', 'hgb', 'crp'] },
  { id: 'diabetes', label: 'Diabetes', markers: ['a1c', 'glucose', 'ldl', 'trig'] },
];

export const DEFAULT_LABS = ['wbc', 'rbc', 'hgb', 'crp'];
export const MAX_LABS = 4;

/** The catalog grouped by panel, for the picker UI. */
export const LAB_GROUPS: { name: string; items: LabMarker[] }[] = (() => {
  const order: string[] = [];
  const by = new Map<string, LabMarker[]>();
  for (const m of LAB_CATALOG) {
    if (!by.has(m.group)) { by.set(m.group, []); order.push(m.group); }
    by.get(m.group)!.push(m);
  }
  return order.map((name) => ({ name, items: by.get(name)! }));
})();
